'use client'

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useUser, useClerk } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import { useCurrentUser } from '@repo/api/identity-service'
import type { UserProfile, Organization } from '@repo/api/identity-service'
import { useStaticOrganizations } from '@/hooks/useStaticOrganizations'

interface AuthContextValue {
  user?: UserProfile
  organizations: Organization[]
  currentOrganization: Organization | null

  // State
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  refreshUser: () => Promise<void>
  switchOrganization: (orgId: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser()
  const clerk = useClerk()
  const router = useRouter()

  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Get identity service user data when Clerk user is available
  const {
    data: identityUser,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useCurrentUser({ enabled: !!clerkUser && clerkLoaded })

  const { data: staticOrgs, isLoading: orgsLoading } = useStaticOrganizations()

  // Update organizations when data changes - filter static orgs by user's orgs
  useEffect(() => {
    if (identityUser?.organizations && staticOrgs?.organizations) {
      const userOrgIds = new Set(identityUser.organizations.map((org) => org.id))
      const userOrganizations = staticOrgs.organizations.filter((org) => userOrgIds.has(org.id))

      setOrganizations(userOrganizations)

      const persistedOrgId = localStorage.getItem('currentOrganizationId')
      const orgToSelect = persistedOrgId
        ? userOrganizations.find((org) => org.id === persistedOrgId) || userOrganizations[0]
        : userOrganizations[0]

      if (orgToSelect) setCurrentOrganization(orgToSelect)
    }
  }, [identityUser, staticOrgs])

  // Clear data when not authenticated
  useEffect(() => {
    if (!clerkUser && clerkLoaded) {
      setOrganizations([])
      setCurrentOrganization(null)
      setError(null)
    }
  }, [clerkUser, clerkLoaded])

  // Set error state
  useEffect(() => {
    if (userError) {
      setError(userError.message || 'Failed to load user data')
    } else {
      setError(null)
    }
  }, [userError])

  const refreshUser = useCallback(async () => {
    await refetchUser()
  }, [refetchUser])

  const switchOrganization = useCallback(
    async (orgId: string) => {
      const org = organizations.find((o) => o.id === orgId)
      if (org) {
        setCurrentOrganization(org)
        localStorage.setItem('currentOrganizationId', orgId)
      }
    },
    [organizations]
  )

  const logout = useCallback(async () => {
    await clerk.signOut()
    setOrganizations([])
    setCurrentOrganization(null)
    setError(null)
    router.push('/login')
  }, [clerk, router])

  const isLoading = !clerkLoaded || userLoading || orgsLoading
  const isAuthenticated = !!clerkUser && !!identityUser

  return (
    <AuthContext.Provider
      value={{
        user: identityUser,
        organizations,
        currentOrganization,
        isAuthenticated,
        isLoading,
        error,
        refreshUser,
        switchOrganization,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuthContext must be used within AuthProvider')

  return context
}
